<template>
  <div class="video-recorder-controls">
    <video
      v-if="fsm.currentState === 'review' && !!recordedVideoUrl"
      class="video-recorder-controls__video"
      :src="recordedVideoUrl"
      data-test-id="VideoRecorderWithControls_VideoReview"
      controls
    />
    <div v-else class="video-recorder-controls__container">
      <div class="video-recorder-controls__recorder">
        <VideoRecorder
          :media-constraints="mediaConstraints"
          :is-recording="fsm.currentState === 'recording'"
          class="video-recorder-controls__video"
          data-test-id="VideoRecorderWithControls_VideoPreview"
          :warning-message-override="
            recordingCountdown.count.value <= 5 && fsm.currentState === 'recording'
              ? c('video_record_with_controls.warning_seconds_left', {
                  placeholders: {
                    seconds: `${recordingCountdown.count.value}`,
                  },
                })
              : undefined
          "
          @recorded="recorded"
          @stream-loaded="onRecorderStreamLoaded"
          @error-starting-recording="onErrorStartingRecording"
        />
        <MediaDevicesSettings
          v-if="fsm.currentState === 'settings'"
          :selected-device-ids="selectedMediaDeviceIds"
          class="video-recorder-controls__settings-overlay"
          data-test-id="VideoRecorderWithControls_Settings"
          @close="fsm.send('CLOSE_SETTINGS')"
          @change="onSettingsChange"
        />

        <div v-if="fsm.currentState === 'idle'" class="video-recorder-controls__actions">
          <MediaDevicesSettingsTrigger
            class="video-recorder-controls__action video-recorder-controls__action--flat video-recorder-controls__action--left"
            data-test-id="VideoRecorderWithControls_SettingsTrigger"
            @click="fsm.send('OPEN_SETTINGS')"
          />
          <button
            class="video-recorder-controls__action video-recorder-controls__action--red video-recorder-controls__action--center"
            data-test-id="VideoRecorderWithControls_RecordButton"
            @click="
              () => {
                fsm.send('START_COUNTDOWN')
                countdownCount = COUNTDOWN_FROM
              }
            "
          >
            <img class="video-recorder-controls__icon" src="../../assets/video-cam.svg" />
          </button>
          <FlipCameraButton
            v-if="supportsFacingMode"
            class="video-recorder-controls__action video-recorder-controls__action--flat video-recorder-controls__action--right"
            data-test-id="VideoRecorderWithControls_FlipCameraButton"
            @flip="toggleFacingMode"
          />
        </div>
        <template v-else-if="fsm.currentState === 'countingDown'">
          <div class="video-recorder-controls__countdown">Get ready</div>
          <div class="video-recorder-controls__actions">
            <button
              class="video-recorder-controls__action video-recorder-controls__action--white video-recorder-controls__action--center"
              data-test-id="VideoRecorderWithControls_StopCountdownButton"
              @click="fsm.send('RESET')"
            >
              <AnimatedCircularProgress :completion="countdownCount / COUNTDOWN_FROM">
                <CountdownToZero
                  :from="COUNTDOWN_FROM"
                  @timeout="() => fsm.send('START_RECORDING')"
                  @count="val => (countdownCount = val)"
                />
              </AnimatedCircularProgress>
            </button>
          </div>
        </template>
        <div v-else-if="fsm.currentState === 'recording'" class="video-recorder-controls__actions">
          <button
            class="video-recorder-controls__action video-recorder-controls__action--white video-recorder-controls__action--center"
            data-test-id="VideoRecorderWithControls_StopRecordingButton"
            @click="fsm.send('STOP_RECORDING')"
          >
            <AnimatedCircularProgress
              background="rgba(255, 255, 255, 0.5)"
              :color="progressColour"
              :completion="1 - recordingCountdown.count.value / MAX_DURATION"
              :warning="recordingCountdown.count.value <= 15"
            >
              <img class="video-recorder-controls__icon" src="../../assets/video-stop.svg" />
            </AnimatedCircularProgress>
          </button>
        </div>
      </div>
    </div>
    <button
      v-if="fsm.currentState === 'review'"
      class="video-recorder-controls__reset"
      data-test-id="VideoRecorderWithControls_ResetButton"
      @click="() => fsm.send('RESET')"
    >
      <img class="video-recorder-controls__icon" src="../../assets/restart-arrow.svg" />
      {{ c('video_record_with_controls.retake') }}
    </button>
  </div>
</template>

<script lang="ts">
import { useFSM } from '@fsmify/vue'
import { computed, defineComponent, onMounted, reactive, ref, watch } from 'vue'

import { c } from '../../../intl'
import AnimatedCircularProgress from '../../../shared/components/dumb/AnimatedCircularProgress.vue'
import CountdownToZero from '../../../shared/components/dumb/CountdownToZero.vue'
import { useStopwatch } from '../../../shared/composables/useStopwatch'
import { useStorageSystem } from '../../../shared/composables/useStorageSystem'
import { useTimer } from '../../../shared/composables/useTimer'
import { useUserMedia, type UseUserMediaConstraints } from '../../composables/useUserMedia'
import VideoRecorder from '../dumb/VideoRecorder.vue'

import FlipCameraButton from './FlipCameraButton.vue'
import MediaDevicesSettings from './MediaDevicesSettings.vue'
import MediaDevicesSettingsTrigger from './MediaDevicesSettingsTrigger.vue'

const COUNTDOWN_FROM = 3
const MAX_DURATION = 120

export default defineComponent({
  components: {
    VideoRecorder,
    CountdownToZero,
    AnimatedCircularProgress,
    FlipCameraButton,
    MediaDevicesSettingsTrigger,
    MediaDevicesSettings,
  },
  props: {
    errorMessage: {
      required: false,
      default: undefined,
      type: String,
    },
  },
  emits: {
    recorded: (_blob: Blob, _durationMs?: number) => true,
    reset: () => true,
    errorStartingRecording: (_error: unknown) => true,
  },
  setup(_props, { emit }) {
    const storage = useStorageSystem()
    const recordedVideoUrl = ref<string | undefined>(undefined)
    const countdownCount = ref<number>(COUNTDOWN_FROM)
    const recordingCountdown = useTimer(MAX_DURATION)

    const fsm = useFSM({
      initialState: 'noVideoStream',
      states: {
        idle: {
          START_COUNTDOWN: 'countingDown',
          OPEN_SETTINGS: 'settings',
        },
        settings: {
          CLOSE_SETTINGS: 'idle',
        },
        countingDown: {
          START_RECORDING: 'recording',
          RESET: 'idle',
        },
        recording: {
          STOP_RECORDING: 'collectingRecording',
        },
        collectingRecording: {
          RECORDING_COLLECTED: 'review',
        },
        review: {
          RESET: 'noVideoStream',
        },
        noVideoStream: {
          VIDEO_STREAM_FOUND: 'idle',
        },
      },
      globalEvents: {
        VIDEO_STREAM_LOST: 'noVideoStream',
      },
    })

    const recordingDuration = useStopwatch()

    fsm.onAfterTransition('START_RECORDING', () => {
      recordingCountdown.reset()
      recordingDuration.start()
    })

    fsm.onAfterTransition('STOP_RECORDING', () => {
      recordingCountdown.clear()
      recordingDuration.stop()
    })

    watch(recordingCountdown.count, () => {
      if (recordingCountdown.count.value <= 0) fsm.send('STOP_RECORDING')
    })

    const progressColour = computed(() =>
      recordingCountdown.count.value < 105 && recordingCountdown.count.value > 15
        ? 'rgba(255, 255, 255, 0.8)'
        : '#E6625B',
    )

    const reset = (): void => {
      if (recordedVideoUrl.value) {
        URL.revokeObjectURL(recordedVideoUrl.value)
        recordedVideoUrl.value = undefined
      }
      emit('reset')
    }
    fsm.onAfterEnterState('noVideoStream', reset)
    fsm.onBeforeEnterState('idle', reset)

    const facingMode = ref<'user' | 'environment'>('user')
    const toggleFacingMode = (): void => {
      facingMode.value = (
        {
          user: 'environment',
          environment: 'user',
        } as const
      )[facingMode.value]

      mediaConstraints.value.video = {
        ...mediaConstraints.value.video,
        facingMode: facingMode.value,
      }
    }

    const selectedMediaDeviceIds = ref<{
      video?: string
      audio?: string
    }>({})

    const onRecorderStreamLoaded = (
      data: false | { videoDeviceId: string; audioDeviceId: string },
    ): void => {
      if (!data) {
        fsm.send('VIDEO_STREAM_LOST')
        return
      }

      selectedMediaDeviceIds.value = {
        video: data.videoDeviceId,
        audio: data.audioDeviceId,
      }
      fsm.send('VIDEO_STREAM_FOUND')
    }

    const mediaConstraints = ref<UseUserMediaConstraints>({
      video: { facingMode: facingMode.value, deviceId: 'default' },
      audio: { enabled: true, deviceId: 'default' },
    })

    const userMedia = useUserMedia(mediaConstraints)

    onMounted(() => {
      setStoredDeviceIds()
    })

    const setStoredDeviceIds = async (): Promise<void> => {
      const storedVideoDeviceId = await storage?.getItem('videoDeviceId')
      const storedAudioDeviceId = await storage?.getItem('audioDeviceId')

      const availableDeviceIds = new Set(
        (await navigator.mediaDevices.enumerateDevices()).map(({ deviceId }) => deviceId),
      )

      const videoDeviceId =
        storedVideoDeviceId && availableDeviceIds.has(storedVideoDeviceId)
          ? storedVideoDeviceId
          : 'default'
      const audioDeviceId =
        storedAudioDeviceId && availableDeviceIds.has(storedAudioDeviceId)
          ? storedAudioDeviceId
          : 'default'

      mediaConstraints.value = {
        video: {
          ...mediaConstraints.value.video,
          deviceId: videoDeviceId,
        },
        audio: {
          ...mediaConstraints.value.audio,
          deviceId: audioDeviceId,
        },
      }

      selectedMediaDeviceIds.value = {
        video: videoDeviceId,
        audio: audioDeviceId,
      }
    }

    const onSettingsChange = (selectedIds: { video?: string; audio?: string }): void => {
      if (!selectedIds.audio || !selectedIds.video)
        throw new Error('expect ids from settings to be defined')

      if (storage !== undefined) {
        storage.setItem('videoDeviceId', selectedIds.video)
        storage.setItem('audioDeviceId', selectedIds.audio)
      }

      selectedMediaDeviceIds.value = selectedIds

      mediaConstraints.value = {
        video: {
          ...mediaConstraints.value.video,
          deviceId: selectedIds.video,
        },
        audio: {
          ...mediaConstraints.value.audio,
          deviceId: selectedIds.audio,
        },
      }
    }

    const supportsFacingMode = computed(
      () => !!userMedia.stream.value?.getVideoTracks()[0]?.getSettings()?.facingMode,
    )

    return {
      fsm: reactive(fsm),
      recorded: (videoBlob: Blob) => {
        recordedVideoUrl.value = URL.createObjectURL(videoBlob)
        fsm.send('RECORDING_COLLECTED')
        emit('recorded', videoBlob)
      },
      reset,
      recordedVideoUrl,
      countdownCount,
      COUNTDOWN_FROM,
      mediaConstraints,
      toggleFacingMode,
      onRecorderStreamLoaded,
      selectedMediaDeviceIds,
      onSettingsChange,
      MAX_DURATION,
      recordingCountdown,
      progressColour,
      supportsFacingMode,
      onErrorStartingRecording: (error: unknown) => emit('errorStartingRecording', error),
      c,
    }
  },
})
</script>

<style lang="postcss">
.video-recorder-controls {
  display: flex;
  flex-direction: column;
  border-radius: 0.5rem;
  overflow: hidden;

  &__container {
    position: relative;
  }

  &__video {
    width: 100%;
    object-position: center;
    aspect-ratio: 4 / 3;
    background-color: black;
  }

  &__recorder {
    position: relative;
  }

  &__settings-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }

  &__countdown {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background-color: rgb(0, 0, 0, 0.4);
    border-radius: 0.5rem;
    color: white;
  }

  &__actions {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-areas: 'left center right';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4.5rem;
    padding: 0 1rem;
  }

  &__action {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background-color: var(--custom-action-button-color, transparent);
    box-shadow: var(--custom-action-box-shadow, 0px 4px 12px rgba(0, 0, 0, 0.16));
    height: 3.5rem;
    width: 3.5rem;
    border-radius: 50%;
    border: none;

    &--flat {
      --custom-action-box-shadow: none;
    }

    &--red {
      --custom-action-button-color: #d0371e;
    }

    &--white {
      --custom-action-button-color: white;
    }

    &--left {
      grid-area: left;
      justify-self: left;
    }

    &--center {
      grid-area: center;
      justify-self: center;
    }

    &--right {
      grid-area: right;
      justify-self: right;
    }
  }

  &__reset {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    border: 0;
    background-color: white;
    cursor: pointer;

    img {
      margin-right: 0.5rem;
    }
  }

  &__icon {
    width: 1.5rem;
    height: 1.5rem;
  }
}
</style>
